import { z } from 'zod';

// ─── member consents vocabularies ───────────────────────────────────────────
//
// Governed enums for the append-only `member_consents` records — the single
// consent record for every consent a member gives (registration documents,
// the Sona AI gate, staff-recorded parental consent). New consent kinds,
// capture surfaces and methods are added here (plus the DB CHECKs) as they
// are ratified, never free-typed at a call site. A new consent is a new
// vocabulary value and a document template, never a new table.

/** Consent kinds a member_consents row can record. */
export const MEMBER_CONSENT_TYPE = [
  'parental',
  'terms_of_use',
  'privacy_policy',
  'notice_of_privacy_practices',
  'telehealth_informed',
  'part2_release',
  'ai_sona',
  // Member-level, or per-call when the evidence names an appointment: whether
  // a recorded telemedicine call is transcribed into a chart note.
  'telemedicine_ai_notes',
  // Member-level only: whether the member's telemedicine calls are recorded.
  'telemedicine_recording',
  // The agreement to receive calls and text messages from Pelago: a clause of
  // the sign-up policies checkbox, governed by the Terms of Use, with no
  // document of its own (ADR-126 Rule 14).
  'calls_and_texts',
  // The TCPA Authorization: Legal's November 2026 calls-and-texts document,
  // evidenced by the sign-up policies checkbox like the Terms of Use.
  'tcpa_authorization',
] as const;
export const memberConsentTypeSchema = z.enum(MEMBER_CONSENT_TYPE);
export type MemberConsentType = z.infer<typeof memberConsentTypeSchema>;

/** Capture surfaces a consent decision can originate from. */
export const MEMBER_CONSENT_SOURCE = [
  'atlas_staff',
  'mobile_app',
  'web_app',
  'system',
  'legacy_import',
] as const;
export const memberConsentSourceSchema = z.enum(MEMBER_CONSENT_SOURCE);
export type MemberConsentSource = z.infer<typeof memberConsentSourceSchema>;

/**
 * How the decision was evidenced. `staff_recorded`: staff flipped it after an
 * out-of-band process (parental consent call). `checkbox`: the member ticked
 * an affirmative control; the typed name and timestamp stamped onto the
 * rendered document are the signature (Part 11 does not apply). `drawn`: the
 * member drew a signature on a signature request.
 */
export const MEMBER_CONSENT_METHOD = ['staff_recorded', 'checkbox', 'drawn'] as const;
export const memberConsentMethodSchema = z.enum(MEMBER_CONSENT_METHOD);
export type MemberConsentMethod = z.infer<typeof memberConsentMethodSchema>;

/**
 * Kinds of member-private document the platform renders and stores.
 * `consent`: the name-and-date stamped PDF evidencing a `member_consents` row.
 * `signed_form`: a document a member signed on a signature request.
 * `drivers_licence`: a government ID image ingested from Healthie or backend2
 * for Rx order backup (not re-filed to the chart).
 */
export const MEMBER_DOCUMENT_KIND = [
  'consent',
  'signed_form',
  'drivers_licence',
  'chat_transcript',
  'chat_attachment',
] as const;
export const memberDocumentKindSchema = z.enum(MEMBER_DOCUMENT_KIND);
export type MemberDocumentKind = z.infer<typeof memberDocumentKindSchema>;

/**
 * Audit evidence stamped onto a consent decision at capture time. Strict so an
 * unknown key fails loudly instead of quietly widening the record. Never
 * carries DOB or IP: `eligibility_state` (primary jurisdiction evidence) and
 * `geoip_state` (best effort, from existing event stamping) are the only
 * location facts, per the Sona consent PRD.
 */
export const memberConsentEvidenceSchema = z
  .object({
    surface: z.enum(['mobile', 'web', 'atlas']).optional(),
    eligibility_state: z.string().length(2).optional(),
    geoip_state: z.string().length(2).optional(),
    // Device-reported affirmative-action time for delayed uploads; created_at remains server receipt time.
    acceptance_id: z.string().min(1).max(64).optional(),
    accepted_at: z.string().datetime({ offset: true }).optional(),
    app_version: z.string().optional(),
    locale: z.string().optional(),
    appointment_id: z.string().uuid().optional(),
    // Which consent capture the decision came from: the sign-up account step
    // or the re-consent a migrating member gives inside the migration flow.
    flow: z.enum(['signup', 'migration']).optional(),
  })
  .strict();
export type MemberConsentEvidence = z.infer<typeof memberConsentEvidenceSchema>;
